import React, { useEffect, useMemo, useRef, useState } from "react";
import { EyeOff, Users, X, Search, Plus, Loader2, Crown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";

// Mode secret d'un dossier (demande d'Anthony du 23/10/2026, maquette
// claude-design/client-modal.jsx) : le dossier n'apparaît plus dans MyALFA
// Worksync (Portefeuille, recherches, Annuaire…) que pour son propriétaire
// et les personnes autorisées ; il reste géré dans MyALFA Link. Droits
// vérifiés côté backend (controllers/clientSecretController.js) :
// activer / quitter = Administrateurs et Experts-comptables ; gérer les
// accès (ajouter, retirer, changer de propriétaire) = le propriétaire et les
// Administrateurs ou Experts-comptables autorisés.

const API = "https://backend-myalfa.vercel.app/api/clients";
export const ROLES_MODE_SECRET = ["ADMINISTRATEUR", "EXPERT_COMPTABLE"];
const ROLE_LIBELLE = {
  ADMINISTRATEUR: "Administrateur",
  EXPERT_COMPTABLE: "Expert-comptable",
  SECRETARIAT: "Secrétariat",
  COLLABORATEUR: "Collaborateur",
};

export const estSecret = (client) => Array.isArray(client?.visiblePar) && client.visiblePar.length > 0;

// Choix « Secret » de la liste du statut (formulaire Modifier) : réservé à
// Anthony, Samuel et Jonathan (demande de Jonathan du 23/10/2026).
const PERSONNES_CHOIX_SECRET = [
  "anthony.hoarau@alfacomptabilite.com",
  "samuel.hoarau@alfacomptabilite.com",
  "jonathan@alfa-partenaires.com",
];
export const voitChoixSecret = (u) => PERSONNES_CHOIX_SECRET.includes(String(u?.email || "").toLowerCase());

const nomCompte = (u) => `${u.name || ""} ${u.lastName || ""}`.trim();
const initiales = (nom) =>
  (nom || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m[0].toUpperCase())
    .join("");
const messageErreur = (e) => e?.response?.data?.message || "L'opération n'a pas pu aboutir. Réessayez.";

// Œil barré à côté du nom (liste du Portefeuille, en-tête de la fiche).
export function MarqueSecret({ taille = 12 }) {
  return (
    <span
      className="inline-grid place-items-center flex-none rounded-md bg-[#9B1B5D]/10 text-[#9B1B5D]"
      style={{ width: taille + 8, height: taille + 8 }}
      title="Dossier secret : accès restreint"
    >
      <EyeOff style={{ width: taille, height: taille }} />
    </span>
  );
}

// État détaillé (personnes autorisées, droits) et actions. Les caches du
// Portefeuille (liste et fiche) sont mis à jour pour que l'œil barré et le
// bandeau suivent sans rechargement.
export function useDossierSecret(client) {
  const queryClient = useQueryClient();
  const id = client?.id;
  const secret = estSecret(client);
  const [etat, setEtat] = useState(null);
  const etatRef = useRef(null);
  etatRef.current = etat;

  useEffect(() => {
    let actif = true;
    if (!id || !secret) {
      setEtat(null);
      return undefined;
    }
    httpClient
      .get(`${API}/${id}/secret`)
      .then(({ data }) => actif && setEtat(data))
      .catch(() => actif && setEtat(null));
    return () => {
      actif = false;
    };
  }, [id, secret]);

  const publier = (data) => {
    setEtat(data.secret ? data : null);
    const champs = { visiblePar: data.acces.map((a) => a.id), secretProprietaire: data.proprietaireId };
    const maj = (c) => (c && c.id === id ? { ...c, ...champs } : c);
    queryClient.setQueryData(["clients"], (l) => (Array.isArray(l) ? l.map(maj) : l));
    queryClient.setQueryData(["client", id], maj);
    queryClient.setQueryData(["client", id, "core"], maj);
  };

  // Mise à jour instantanée : l'écran change tout de suite (`provisoire`), un
  // toast suit l'enregistrement. Refus du serveur : état d'avant, toast
  // d'erreur, puis état réel relu (d'autres changements ont pu passer).
  const executer = async ({ provisoire, requete, enCours, ok }) => {
    const avant = etatRef.current;
    if (provisoire) publier(provisoire);
    const toast = showStatusToast({ status: "loading", titre: "Dossier secret", message: enCours });
    try {
      const { data } = await requete();
      publier(data);
      toast.update({ status: "success", titre: "Dossier secret", message: ok });
      return data;
    } catch (e) {
      if (provisoire && avant) publier(avant);
      toast.update({ status: "error", titre: "Dossier secret", message: messageErreur(e) });
      if (provisoire) {
        httpClient
          .get(`${API}/${id}/secret`)
          .then(({ data }) => publier(data))
          .catch(() => {});
      }
      throw e;
    }
  };
  const enregistrerAcces = (acces) => () => httpClient.put(`${API}/${id}/secret/acces`, { acces: acces.map((a) => a.id) });

  return {
    etat,
    activer: () =>
      executer({ requete: () => httpClient.post(`${API}/${id}/secret`), enCours: "Passage en mode Secret…", ok: "Dossier passé en mode Secret" }),
    quitter: () =>
      executer({ requete: () => httpClient.delete(`${API}/${id}/secret`), enCours: "Sortie du mode Secret…", ok: "Le dossier est de nouveau visible par tous" }),
    ajouterAcces: (u) => {
      const base = etatRef.current;
      const acces = [...base.acces, { id: u.id, nom: nomCompte(u), role: u.role, poste: u.poste || null, proprietaire: false }];
      return executer({ provisoire: { ...base, acces }, requete: enregistrerAcces(acces), enCours: `Ajout de ${nomCompte(u)}…`, ok: `Accès donné à ${nomCompte(u)}` });
    },
    retirerAcces: (a) => {
      const base = etatRef.current;
      const acces = base.acces.filter((x) => x.id !== a.id);
      return executer({ provisoire: { ...base, acces }, requete: enregistrerAcces(acces), enCours: `Retrait de ${a.nom}…`, ok: `Accès retiré à ${a.nom}` });
    },
    changerProprietaire: (a) => {
      const base = etatRef.current;
      const acces = base.acces.map((x) => ({ ...x, proprietaire: x.id === a.id })).sort((x, y) => y.proprietaire - x.proprietaire);
      return executer({
        provisoire: { ...base, proprietaireId: a.id, acces },
        requete: () => httpClient.put(`${API}/${id}/secret/proprietaire`, { proprietaireId: a.id }),
        enCours: `${a.nom} devient propriétaire…`,
        ok: `${a.nom} est maintenant propriétaire`,
      });
    },
  };
}

// Bandeau sous l'en-tête de la fiche.
export function BandeauSecret({ client, etat, onGerer }) {
  const acces = etat?.acces || [];
  const nb = acces.length || client.visiblePar?.length || 0;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#9B1B5D]/20 bg-[#9B1B5D]/[0.04] px-3 py-2">
      <span className="grid h-7 w-7 flex-none place-items-center rounded-lg bg-[#9B1B5D]/10 text-[#9B1B5D]">
        <EyeOff className="h-4 w-4" />
      </span>
      <span className="min-w-[160px] flex-1 text-[13px] text-slate-600">
        <b className="text-[#9B1B5D]">Dossier secret</b> · accès limité à {nb} personne{nb > 1 ? "s" : ""}
      </span>
      <span className="flex pl-1.5">
        {acces.slice(0, 5).map((a) => (
          <span
            key={a.id}
            title={a.nom}
            className="-ml-1.5 grid h-7 w-7 place-items-center rounded-full bg-[#EFE6E2] text-[11px] font-bold text-[#9B1B5D] ring-2 ring-white"
          >
            {initiales(a.nom)}
          </span>
        ))}
        {acces.length > 5 && (
          <span className="-ml-1.5 grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-[11px] font-bold text-slate-400 ring-2 ring-white">
            +{acces.length - 5}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={onGerer}
        className="flex h-8 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
      >
        <Users className="h-3.5 w-3.5" /> {etat?.peutGerer ? "Gérer les accès" : "Voir les accès"}
      </button>
    </div>
  );
}

// Fenêtre « Accès au dossier secret ». Le propriétaire et les
// Administrateurs ou Experts-comptables autorisés ajoutent, retirent des
// collaborateurs ou désignent un autre propriétaire ; les autres personnes
// autorisées voient la liste. Chaque changement s'affiche tout de suite, un
// toast confirme l'enregistrement (voir useDossierSecret).
export function FenetreAccesSecret({ client, etat, users, actions, onClose }) {
  const [q, setQ] = useState("");
  const acces = useMemo(() => etat?.acces || [], [etat]);
  const ids = useMemo(() => new Set(acces.map((a) => a.id)), [acces]);
  const peutGerer = !!etat?.peutGerer;

  const trouves = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return (users || [])
      .filter((u) => !ids.has(u.id) && u.status !== "Archivé")
      .filter((u) => `${nomCompte(u)} ${ROLE_LIBELLE[u.role] || u.role || ""} ${u.poste || ""}`.toLowerCase().includes(t))
      .slice(0, 6);
  }, [q, users, ids]);

  useEffect(() => {
    const echap = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [onClose]);

  // Erreurs : déjà signalées par le toast, et la liste est remise en état.
  const sansErreur = (p) => p.catch(() => {});
  const ajouter = (u) => {
    setQ("");
    sansErreur(actions.ajouterAcces(u));
  };
  const rendreProprietaire = async (a) => {
    const ok = await dialog.confirm(`${a.nom} deviendra propriétaire du dossier. L'actuel propriétaire garde son accès.`, {
      title: "Changer de propriétaire",
      confirmLabel: "Rendre propriétaire",
      variant: "warning",
    });
    if (ok) sansErreur(actions.changerProprietaire(a));
  };

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex max-h-[min(640px,calc(100vh-48px))] w-[460px] max-w-full flex-col rounded-xl bg-white shadow-xl" role="dialog" aria-modal="true" aria-label="Accès au dossier secret">
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 pb-3.5 pt-4">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-[#9B1B5D]/10 text-[#9B1B5D]">
            <EyeOff className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-base font-semibold text-slate-900">Accès au dossier secret</h3>
            <small className="block truncate text-[12.5px] text-slate-500">{client.nom}</small>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3 overflow-auto px-5 pb-5 pt-4">
          {peutGerer && (
            <div className="relative flex h-10 items-center gap-2 rounded-[9px] border border-slate-200 bg-white px-3 text-slate-400 focus-within:border-[#9B1B5D]">
              <Search className="h-3.5 w-3.5" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ajouter un collaborateur…"
                className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-slate-900 outline-none"
              />
              {trouves.length > 0 && (
                <div className="absolute left-[-1px] right-[-1px] top-[calc(100%+4px)] z-10 flex flex-col rounded-[9px] border border-slate-200 bg-white p-1 shadow-lg">
                  {trouves.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => ajouter(u)}
                      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-slate-400 hover:bg-[#F7F2EF] hover:text-[#9B1B5D]"
                    >
                      <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[#EFE6E2] text-[11px] font-bold text-[#9B1B5D]">{initiales(nomCompte(u))}</span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <b className="text-[13.5px] font-semibold text-slate-900">{nomCompte(u)}</b>
                        <small className="truncate text-xs text-slate-500">{ROLE_LIBELLE[u.role] || u.role || "—"}</small>
                      </span>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-1 flex gap-1.5 text-[11.5px] font-bold uppercase tracking-wider text-slate-500">
            Ont accès <span className="text-[#9B1B5D]">{acces.length}</span>
          </div>
          <div className="flex flex-col rounded-[10px] border border-slate-200">
            {!etat && (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
              </div>
            )}
            {acces.map((a, i) => (
              <div key={a.id} className={`flex items-center gap-2.5 px-2.5 py-2 ${i ? "border-t border-slate-200" : ""}`}>
                <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[#EFE6E2] text-[11px] font-bold text-[#9B1B5D]">{initiales(a.nom)}</span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[13.5px] font-semibold text-slate-900">{a.nom}</b>
                  <small className="truncate text-xs text-slate-500">{ROLE_LIBELLE[a.role] || a.role || "—"}</small>
                </span>
                {a.proprietaire ? (
                  <span className="rounded-md bg-[#F4EFEC] px-2 py-0.5 text-[11.5px] font-semibold text-slate-500">Propriétaire</span>
                ) : (
                  peutGerer && (
                    <>
                      <button
                        type="button"
                        title="Désigner comme propriétaire du dossier"
                        aria-label={`Rendre ${a.nom} propriétaire`}
                        onClick={() => rendreProprietaire(a)}
                        className="flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] font-semibold text-slate-400 hover:bg-[#F7F2EF] hover:text-[#9B1B5D]"
                      >
                        <Crown className="h-3.5 w-3.5" /> Rendre propriétaire
                      </button>
                      <button
                        type="button"
                        title="Retirer l'accès"
                        aria-label={`Retirer l'accès de ${a.nom}`}
                        onClick={() => sansErreur(actions.retirerAcces(a))}
                        className="grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )
                )}
              </div>
            ))}
          </div>
          <p className="m-0 text-xs leading-relaxed text-slate-500">
            Les autres collaborateurs ne voient pas ce dossier dans MyALFA Worksync : Portefeuille, recherches, Annuaire. Il reste accessible à ses contacts dans MyALFA Link.
            {!peutGerer && " Seuls le propriétaire du dossier et les Administrateurs ou Experts-comptables autorisés peuvent modifier cette liste."}
          </p>
        </div>
      </div>
    </div>
  );
}

